import React, { useCallback, useState, useRef, useEffect } from "react";
import { ReactComponent as FilterIcon } from "../../../assets/icons/Filter.svg";
import { ReactComponent as PrintIcon } from "../../../assets/icons/PrintIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/DownloadIcon.svg";
import { ReactComponent as FullViewIcon } from "../../../assets/icons/FullViewIcon.svg";
import { ReactComponent as NormalViewIcon } from "../../../assets/icons/NormalViewIcon.svg";
import Dropdown from "./Dropdown";
import { Button } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CSVIcon from "../../../assets/icons/CSVicon.svg";
import PDFIcon from "../../../assets/icons/PDFicon.svg";
import {
  convertToCSV,
  convertTableSnapshotToCSV,
  exportLogsToPDF,
  exportTableSnapshotToPDF,
  getRenderedTableSnapshot,
  downloadFile,
} from "../../utils/converter";
const TableToolbar = ({
  spreadTable,
  onSpreadTableToggle,
  onAddClick,
  onPrint,
  onPrintClick,
  displayData,
  columns,
  // Generic filter fields: [{ key, label, type: 'text'|'select', value, onChange, placeholder?, options?: [{value,name}] }]
  filterFields,
  // Legacy user-management filter props (kept for backward compat)
  deliveryAddressFilter,
  setDeliveryAddressFilter,
  emailModeFilter,
  setEmailModeFilter,
  smsModeFilter,
  setSmsModeFilter,
  emailModeOptions,
  smsModeOptions,
}) => {
  // Build resolved filter fields: prefer generic filterFields, fall back to legacy props
  const resolvedFilterFields = filterFields || [
    {
      key: "deliveryAddress",
      label: "Delivery Address",
      type: "text",
      value: deliveryAddressFilter || "",
      onChange: (v) => setDeliveryAddressFilter && setDeliveryAddressFilter(v),
      placeholder: "Enter delivery address",
    },
    {
      key: "emailMode",
      label: "Email Mode",
      type: "select",
      value: emailModeFilter || "all",
      onChange: (v) => setEmailModeFilter && setEmailModeFilter(v),
      options: emailModeOptions || [],
    },
    {
      key: "smsMode",
      label: "SMS Mode",
      type: "select",
      value: smsModeFilter || "all",
      onChange: (v) => setSmsModeFilter && setSmsModeFilter(v),
      options: smsModeOptions || [],
    },
  ];
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState("csv");
  const exportModalRef = useRef(null);
  const exportButtonRef = useRef(null);
  const toolbarRootRef = useRef(null);

  const getScopedTableElement = useCallback(() => {
    const toolbarRoot = toolbarRootRef.current;
    const scopedContainer = toolbarRoot?.closest(
      ".dashboard-table-area, .logs-table-area",
    );

    if (scopedContainer) {
      const table = scopedContainer.querySelector("table");
      if (table) return table;
    }

    return document.querySelector(
      ".dashboard-table-area table, .logs-table-area table",
    );
  }, []);

  // Close export modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showExportModal &&
        exportModalRef.current &&
        exportButtonRef.current &&
        !exportModalRef.current.contains(event.target) &&
        !exportButtonRef.current.contains(event.target)
      ) {
        setShowExportModal(false);
      }
    };

    if (showExportModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportModal]);
  const handlePrintTable = useCallback(() => {
    const table = getScopedTableElement();
    if (!table) {
      const printHandler = onPrintClick || onPrint;
      if (printHandler) printHandler();
      return;
    }

    const tableClone = table.cloneNode(true);
    const ths = tableClone.querySelectorAll("th");
    ths.forEach((th) => {
      th.querySelectorAll(
        "svg, i, span[class*=icon], .fa, .svg-inline--fa",
      ).forEach((el) => el.remove());
    });

    const printWindow = window.open("", "", "width=900,height=700");
    if (!printWindow) return;

    printWindow.document.write("<html><head><title>Print Table</title>");
    printWindow.document.write(
      "<style>body{font-family:sans-serif;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;}</style>",
    );
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      "<table>" +
        (tableClone.tHead ? tableClone.tHead.outerHTML : "") +
        (tableClone.tBodies[0] ? tableClone.tBodies[0].outerHTML : "") +
        "</table>",
    );
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [getScopedTableElement, onPrint, onPrintClick]);

  const handleExportCSV = useCallback(async () => {
    const table = getScopedTableElement();
    const snapshot = getRenderedTableSnapshot(table);

    if (snapshot?.headers?.length) {
      const csv = convertTableSnapshotToCSV(snapshot);
      if (csv) {
        downloadFile("user-management.csv", csv, "text/csv");
        return;
      }
    }

    if (displayData && columns) {
      const csv = await convertToCSV(displayData, columns);
      downloadFile("user-management.csv", csv, "text/csv");
    }
  }, [columns, displayData, getScopedTableElement]);

  const handleExportPDF = useCallback(() => {
    const table = getScopedTableElement();
    const snapshot = getRenderedTableSnapshot(table);

    if (snapshot?.headers?.length) {
      exportTableSnapshotToPDF(snapshot, "user-management", "PBXScribe Transcription Logs");
      return;
    }

    if (displayData && columns) {
      exportLogsToPDF(displayData, columns, "user-management");
    }
  }, [columns, displayData, getScopedTableElement]);

  return (
    <>
      <div
        className="flex items-center max-[400px]:justify-between gap-3 sm:gap-2"
        ref={toolbarRootRef}
      >
        <div className="h-6 w-0.5 bg-line mx-2"></div>
        <button
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
          onClick={() => setShowFilterModal(true)}
          aria-label="Filter"
        >
          <FilterIcon />
        </button>

        <button
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-all duration-200"
          onClick={handlePrintTable}
          aria-label="Print"
        >
          <PrintIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        <div className="relative">
          <button
            ref={exportButtonRef}
            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-all duration-200"
            onClick={(e) => {
              e.stopPropagation();
              setShowExportModal((v) => !v);
            }}
            aria-label="Download"
          >
            <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          {showExportModal && (
            <div
              ref={exportModalRef}
              className="absolute max-[400px]:left-1/2 max-[400px]:-translate-x-1/2 right-0 mt-2 z-50 bg-white rounded-xl shadow-lg border border-line min-w-[240px] p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-base font-bold text-primary">
                  Select Export
                </span>
                <button
                  className="text-primary hover:text-gray-700 leading-tight border border-primary rounded-full !w-5 !h-5 flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExportModal(false);
                  }}
                  aria-label="Close"
                >
                  <CloseIcon fontSize="small" className="!w-4" />
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed text-sm font-bold ${
                    exportType === "csv"
                      ? "bg-content/20 border-content text-primary"
                      : "bg-white border-content text-primary"
                  }`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setExportType("csv");
                    await handleExportCSV();
                    setShowExportModal(false);
                  }}
                >
                  <img className="w-fit h-fit" src={CSVIcon} alt="CSV" />
                  CSV
                </button>
                <button
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed text-sm font-bold ${
                    exportType === "pdf"
                      ? "bg-content/20 border-content text-primary"
                      : "bg-white border-content text-primary"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExportType("pdf");
                    handleExportPDF();
                    setShowExportModal(false);
                  }}
                >
                  <img className="w-fit h-fit" src={PDFIcon} alt="PDF" />
                  PDF
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          className={`p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-all duration-200 ${
            spreadTable ? "bg-gray-200" : ""
          }`}
          onClick={onSpreadTableToggle}
          aria-label={spreadTable ? "Normal View" : "Full View"}
        >
          {spreadTable ? (
            <NormalViewIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          ) : (
            <FullViewIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </button>

        <div className="h-6 w-0.5 bg-line mr-2"></div>
      </div>
      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 !z-50 flex items-center justify-center bg-black bg-opacity-40 px-5">
          <div className="bg-white rounded-lg shadow-xl w-[400px] max-w-full p-6 relative">
            <button
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
              onClick={() => setShowFilterModal(false)}
            >
              <FilterIcon />
            </button>
            <h2 className="text-lg font-bold mb-4 text-primary">
              Advanced Filter
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setShowFilterModal(false);
              }}
              className="space-y-4"
            >
              {resolvedFilterFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">
                    {field.label}
                  </label>
                  {field.type === "select" ? (
                    <select
                      className="w-full border border-line rounded px-3 py-2 text-sm"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {(field.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="w-full border border-line rounded px-3 py-2 text-sm"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder={field.placeholder || ""}
                    />
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                  onClick={() => setShowFilterModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-primary text-white"
                >
                  Apply
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TableToolbar;
